import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Plus, Globe, Trash2, Pencil, Shield, Users, Eye } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/axios';
import { toast } from 'sonner';

const SuperAdminTenants = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/tenants');
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setTenants(rows);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load tenants');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleDeleteTenant = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await api.delete(`/tenants/${deleteTarget.id}`);
      toast.success('Tenant deleted');
      setDeleteTarget(null);
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete tenant');
    } finally {
      setDeleting(false);
    }
  };

  const getTenantStatusClass = (tenant) =>
    tenant.status === 'active' || tenant.is_active
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-1 rounded text-xs font-bold'
      : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 px-2 py-1 rounded text-xs font-bold';

  return (
    <DashboardLayout userRole="super_admin">
      <div className="w-full min-h-0 bg-slate-50 dark:bg-slate-900 pb-2">
        <div className="w-full max-w-none mx-auto space-y-6">
          {/* Header Card */}
          <div className="rounded-2xl tenant-brand-gradient text-white p-6 shadow-lg shadow-indigo-500/20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Manage Tenants</h1>
                  <p className="text-white/80 text-sm mt-1">Configure client branding, domains, and access.</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/super-admin/tenants/new')}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-white text-slate-900 font-bold text-sm shadow-sm hover:bg-slate-50 transition-all active:scale-95"
              >
                <Plus className="w-4 h-4" /> Add New Tenant
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Total', val: tenants.length, icon: Building, color: 'blue' },
              { label: 'Active', val: tenants.filter(t => t.is_active).length, icon: Shield, color: 'emerald' },
              { label: 'Domains', val: tenants.reduce((sum, t) => sum + (t.allowed_domains?.length || 0), 0), icon: Globe, color: 'violet' },
              { label: 'Users', val: tenants.reduce((sum, t) => sum + Number(t.users_count || 0), 0), icon: Users, color: 'amber' }
            ].map((stat, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 rounded-2xl shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{stat.label}</p>
                    <p className="text-xl font-black text-slate-900 dark:text-white mt-1">{stat.val}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl bg-${stat.color}-500/10 text-${stat.color}-600`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Card */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 dark:bg-slate-700/30 border-b border-slate-200 dark:border-slate-700">
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Company</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Portal</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Branding</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                  {loading ? (
                    <tr><td colSpan="5" className="p-10 text-center text-slate-400">Loading records...</td></tr>
                  ) : tenants.map((tenant) => (
                    <tr key={tenant.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                            <Building className="w-4 h-4 text-slate-500" />
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 dark:text-white">{tenant.name}</div>
                            <div className="text-xs text-slate-500">{tenant.users_count || 0} users</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300">{tenant.portal_name}</div>
                        <div className="text-xs font-mono text-indigo-500">{tenant.slug}</div>
                      </td>
                      <td className="px-6 py-4">
                         <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded border border-white/20 shadow-sm" style={{ background: `linear-gradient(135deg, ${tenant.primary_color}, ${tenant.accent_color})` }} />
                            <span className="text-xs text-slate-500 font-medium">{tenant.support_email}</span>
                         </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={getTenantStatusClass(tenant)}>{tenant.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            title="View"
                            aria-label="View tenant"
                            onClick={() => navigate(`/super-admin/tenants/${tenant.id}`)}
                            className="p-2 text-slate-400 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-500/10 rounded-lg transition-all"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            aria-label="Edit tenant"
                            onClick={() => navigate(`/super-admin/tenants/${tenant.id}/edit`)}
                            className="p-2 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-lg transition-all"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            aria-label="Delete tenant"
                            onClick={() => setDeleteTarget(tenant)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-[120] bg-black/45 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xl p-5 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Delete Tenant?</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                This will remove <span className="font-semibold">{deleteTarget.name}</span> and cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteTenant}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SuperAdminTenants;